import { CurrencyCode } from '@shared/hooks/useGeoLocale';

export type NumberFormatOptions = Readonly<
  Intl.NumberFormatOptions & {
    as?: 'number' | 'currency' | 'percent';
    precision?: number;
  }
>;

/**
 * Currency format options:
 * - 'auto': Uses 'short' if currencies match, 'explicit' if they don't
 * - 'short': Strips country codes from symbols (e.g., $ instead of AU$)
 * - 'explicit': Shows symbol + ISO code (e.g., $1,234.56 USD)
 * - 'none': No currency symbol, just the number
 */
export type CurrencyFormat = 'auto' | 'short' | 'explicit' | 'none';
export type CurrencyFormatOptions = Readonly<
  NumberFormatOptions & {
    form?: CurrencyFormat;
  }
>;

export class MissingCurrencyCodeError extends Error {
  constructor(additionalMessage = '') {
    const baseErrorMessage = 'No currency code provided.';
    super(additionalMessage === '' ? baseErrorMessage : `${baseErrorMessage} ${additionalMessage}`);
  }
}
export class MissingCountryError extends Error {
  constructor(additionalMessage = '') {
    const baseErrorMessage = 'No country code provided.';
    super(additionalMessage === '' ? baseErrorMessage : `${baseErrorMessage} ${additionalMessage}`);
  }
}

export type CurrencyError = MissingCurrencyCodeError | MissingCountryError;

export type CurrencyArgs = Readonly<{
  locale: string;
  defaultCurrency?: string;
}>;

export type CurrencyConverterArgs = Readonly<{
  targetCurrency?: CurrencyCode;
}>;

export type CurrencyFormatterArgs = Readonly<{
  /** The stringified price is expressed in Major Units */
  priceFloatingPointDecimalString: string;
  priceCurrencyCode?: string;
  formatForm: CurrencyFormat;
  formatPrecision?: number;
  canShowFreeLabel?: boolean;
  shouldRoundPriceUp?: boolean;
  shouldCalculateCharmPrice?: boolean;
  hideZeroDecimals?: boolean;
}>;
